import { URLS } from '../constants'
import {
  GET_USAGE_LOG_BEGIN, GET_USAGE_LOG_SUCCESS, GET_USAGE_LOG_FAILURE,
  GET_SCORE_METRICS_BEGIN, GET_SCORE_METRICS_SUCCESS, GET_SCORE_METRICS_FAILURE,
  GET_UNIQUE_DOCUMENTS_BEGIN, GET_UNIQUE_DOCUMENTS_SUCCESS, GET_UNIQUE_DOCUMENTS_FAILURE,
  GET_SCORE_BY_DOCUMENT_BEGIN, GET_SCORE_BY_DOCUMENT_SUCCESS, GET_SCORE_BY_DOCUMENT_FAILURE,
  GET_LOGS_BY_DOCUMENT_BEGIN, GET_LOGS_BY_DOCUMENT_SUCCESS, GET_LOGS_BY_DOCUMENT_FAILURE,
  GET_SCORE_METRICS_BY_DATE_BEGIN, GET_SCORE_METRICS_BY_DATE_SUCCESS, GET_SCORE_METRICS_BY_DATE_FAILURE,
  CLEAN_USAGE_LOG_METRICS
} from '../constants/ActionTypes'

import { dispatchFetchGet } from './ActionUtil'

// Get Company Usage Log (Existing)
const acGetCompanyUsageLogBegin = () => ({ type: GET_USAGE_LOG_BEGIN })
const acGetCompanyUsageLogSuccess = data => ({ type: GET_USAGE_LOG_SUCCESS, payload: data })
const acGetCompanyUsageLogFailure = err => ({ type: GET_USAGE_LOG_FAILURE, payload: err })

/**
 * Retrieves the company usage log for a given date range.
 *
 * @author Isadora Botassari
 * @param {Object} options - The options for retrieving the usage log.
 * @param {string} options.startDate - The start date of the date range.
 * @param {string} options.endDate - The end date of the date range.
 * @returns {Promise} A promise that resolves with the usage log data or rejects with an error.
 */
export const acGetCompanyUsageLog = ({ startDate, endDate }) => {
  return dispatchFetchGet(
    `${URLS.USAGE_LOG}?startDate=${startDate}&endDate=${endDate}`,
    acGetCompanyUsageLogBegin,
    acGetCompanyUsageLogSuccess,
    acGetCompanyUsageLogFailure
  )
}

// Score Metrics Actions (New)
const acGetScoreMetricsBegin = () => ({ type: GET_SCORE_METRICS_BEGIN })
const acGetScoreMetricsSuccess = data => ({ type: GET_SCORE_METRICS_SUCCESS, payload: data })
const acGetScoreMetricsFailure = err => ({ type: GET_SCORE_METRICS_FAILURE, payload: err })

/**
 * @description Dispatches a GET request to retrieve score metrics for dashboard.
 *
 * @author pvborgesz
 * @date 2025-06-17
 * @param {Object} filters - Object containing startDate, endDate, and document filters
 * @param {string} filters.startDate - Start date in YYYY-MM-DD format
 * @param {string} filters.endDate - End date in YYYY-MM-DD format
 * @param {string} filters.document - Optional CNPJ filter
 */
export const acGetScoreMetrics = (filters = {}) => {
  const { startDate, endDate, document } = filters
  const queryParams = new URLSearchParams()

  if (startDate) queryParams.append('startDate', startDate)
  if (endDate) queryParams.append('endDate', endDate)
  if (document) queryParams.append('document', document)

  const url = `${URLS.USAGE_LOG}/metrics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  return dispatchFetchGet(
    url,
    acGetScoreMetricsBegin,
    acGetScoreMetricsSuccess,
    acGetScoreMetricsFailure
  )
}

// Unique Documents Actions (New)
const acGetUniqueDocumentsBegin = () => ({ type: GET_UNIQUE_DOCUMENTS_BEGIN })
const acGetUniqueDocumentsSuccess = data => ({ type: GET_UNIQUE_DOCUMENTS_SUCCESS, payload: data })
const acGetUniqueDocumentsFailure = err => ({ type: GET_UNIQUE_DOCUMENTS_FAILURE, payload: err })

/**
 * @description Dispatches a GET request to retrieve all unique documents (CNPJs).
 *
 * @author pvborgesz
 * @date 2025-06-17
 */
export const acGetUniqueDocuments = (document) => {

    let url = `${URLS.USAGE_LOG}/documents`

    if (document) {
        url = new URL(url, location.origin)
        url.searchParams.set("document", document)
    }

    return dispatchFetchGet(
        url,
        acGetUniqueDocumentsBegin,
        acGetUniqueDocumentsSuccess,
        acGetUniqueDocumentsFailure
    )
}

// Score by Document Actions (New)
const acGetScoreByDocumentBegin = () => ({ type: GET_SCORE_BY_DOCUMENT_BEGIN })
const acGetScoreByDocumentSuccess = data => ({ type: GET_SCORE_BY_DOCUMENT_SUCCESS, payload: data })
const acGetScoreByDocumentFailure = err => ({ type: GET_SCORE_BY_DOCUMENT_FAILURE, payload: err })

/**
 * @description Dispatches a GET request to retrieve the latest score for a specific document.
 *
 * @author pvborgesz
 * @date 2025-06-17
 * @param {string} document - The CNPJ to search for
 */
export const acGetScoreByDocument = (document) => {
  return dispatchFetchGet(
    `${URLS.USAGE_LOG}/score/${document}`,
    acGetScoreByDocumentBegin,
    acGetScoreByDocumentSuccess,
    acGetScoreByDocumentFailure
  )
}

// Logs by Document Actions (New)
const acGetLogsByDocumentBegin = () => ({ type: GET_LOGS_BY_DOCUMENT_BEGIN })
const acGetLogsByDocumentSuccess = data => ({ type: GET_LOGS_BY_DOCUMENT_SUCCESS, payload: data })
const acGetLogsByDocumentFailure = err => ({ type: GET_LOGS_BY_DOCUMENT_FAILURE, payload: err })

/**
 * @description Dispatches a GET request to retrieve usage logs for a specific document.
 *
 * @author pvborgesz
 * @date 2025-06-17
 * @param {Object} filters - Object containing document and date filters
 * @param {string} filters.document - The CNPJ to search for
 * @param {string} filters.startDate - Start date in YYYY-MM-DD format
 * @param {string} filters.endDate - End date in YYYY-MM-DD format
 */
export const acGetLogsByDocument = (filters = {}) => {
  const { document, startDate, endDate } = filters
  const queryParams = new URLSearchParams()

  if (document) queryParams.append('document', document)
  if (startDate) queryParams.append('startDate', startDate)
  if (endDate) queryParams.append('endDate', endDate)

  const url = `${URLS.USAGE_LOG}/by-document?${queryParams.toString()}`

  return dispatchFetchGet(
    url,
    acGetLogsByDocumentBegin,
    acGetLogsByDocumentSuccess,
    acGetLogsByDocumentFailure
  )
}

const acGetScoreMetricsByDateBegin = () => ({ type: GET_SCORE_METRICS_BY_DATE_BEGIN })
const acGetScoreMetricsByDateSuccess = data => ({ type: GET_SCORE_METRICS_BY_DATE_SUCCESS, payload: data })
const acGetScoreMetricsByDateFailure = err => ({ type: GET_SCORE_METRICS_BY_DATE_FAILURE, payload: err })

export const acGetScoreMetricsByDate = (filters = {}) => {
  const { startDate, endDate, document } = filters
  const queryParams = new URLSearchParams()

  if (startDate) queryParams.append('startDate', startDate)
  if (endDate) queryParams.append('endDate', endDate)
  if (document) queryParams.append('document', document)

  const url = `${URLS.USAGE_LOG}/metrics-by-date${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  return dispatchFetchGet(
    url,
    acGetScoreMetricsByDateBegin,
    acGetScoreMetricsByDateSuccess,
    acGetScoreMetricsByDateFailure
  )
}

export const acCleanUsageLogMetrics = () => ({ type: CLEAN_USAGE_LOG_METRICS })
